import { useState, type FC } from "react";
import styled from "styled-components";
import { policyDescriptions } from "@shared_packages/toolkit/types";
import {
  Avatar,
  AvatarSizeVariant,
  Dropdown,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { Modal } from "../../../../components/modal/Modal";
import WarningModal from "../../../../components/ui-kit/modal/WarningModal";
import { DetailCardsGrid } from "../../components/detail-cards";
import { ConfigurableDetailCard } from "../../components/detail-cards/config-detail-card";
import {
  buildUserMembershipCardConfig,
  buildUserPersonalDetailsCardConfig,
} from "../config/detailsCardConfig";
import {
  CompanyMembershipFullApiType,
  RoleApiType,
  UserWithCompanyMemberships,
} from "../../../../util/types";

interface UserDetailsProps {
  userData: UserWithCompanyMemberships;
  onActivateClick: (companyId: string) => Promise<void>;
  onBanClick: (companyId: string) => Promise<void>;
  onEditClick: () => void;
  onSendEmailVerification: () => Promise<void>;
  onSendPasswordReset: () => Promise<void>;
  onMarkEmailAsVerified: () => Promise<void>;
  /** When false, hide admin actions and edit (viewUsers only). */
  canManageUsers?: boolean;
}

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
  margin-bottom: 15px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ActionsBlock = styled.div``;

const RolePoliciesModal = styled.div`
  max-width: 600px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px;
`;

const UserDetails: FC<UserDetailsProps> = ({
  userData,
  onBanClick,
  onActivateClick,
  onEditClick,
  onSendEmailVerification,
  onSendPasswordReset,
  onMarkEmailAsVerified,
  canManageUsers = true,
}) => {
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    action: string;
    onConfirm: () => void;
  } | null>(null);
  const [roleModal, setRoleModal] = useState<{
    isOpen: boolean;
    role: RoleApiType;
    policies: string[];
  } | null>(null);

  if (!userData) {
    return <></>;
  }

  const handleActionClick = (action: string, onConfirm: () => void) => {
    setConfirmationModal({ isOpen: true, action, onConfirm });
  };

  const closeConfirmationModal = () => setConfirmationModal(null);

  const handleRoleClick = (role: RoleApiType) => {
    const policies = (role.policies ?? {}) as unknown as Record<
      string,
      boolean
    >;
    const policyKeys = Object.keys(policies);
    const sortedPolicies = policyKeys.sort((a, b) => {
      const aEnabled = policies[a];
      const bEnabled = policies[b];
      if (aEnabled && !bEnabled) return -1;
      if (!aEnabled && bEnabled) return 1;
      return 0;
    });
    setRoleModal({ isOpen: true, role, policies: sortedPolicies });
  };

  const closeRoleModal = () => setRoleModal(null);

  const actions = [
    {
      label: "Mark email as verified",
      value: "mark-verified",
      disabled: userData.emailVerified || !userData.identityProviderReference,
      tooltip: userData.emailVerified
        ? "Email is already verified"
        : !userData.identityProviderReference
        ? "Identity provider reference is missing"
        : undefined,
    },
    {
      label: "Send email verification email",
      value: "send-verification",
      disabled: userData.emailVerified || !userData.identityProviderReference,
      tooltip: userData.emailVerified
        ? "Email is already verified"
        : !userData.identityProviderReference
        ? "Identity provider reference is missing"
        : undefined,
    },
    {
      label: "Send password reset email",
      value: "send-reset",
      disabled:
        userData.emailVerified === false || !userData.identityProviderReference,
      tooltip:
        userData.emailVerified === false
          ? "Email must be verified first"
          : !userData.identityProviderReference
          ? "Identity provider reference is missing"
          : undefined,
    },
  ];

  const enabledActions = actions.filter((action) => !action.disabled);

  const handleActionSelect = (action: any) => {
    switch (action.value) {
      case "mark-verified":
        handleActionClick("mark email as verified", onMarkEmailAsVerified);
        break;
      case "send-verification":
        handleActionClick(
          "send email verification email",
          onSendEmailVerification
        );
        break;
      case "send-reset":
        handleActionClick("send password reset email", onSendPasswordReset);
        break;
    }
  };

  const personalDetailsConfig = buildUserPersonalDetailsCardConfig({
    canManageUsers,
    onEditClick,
  });

  return (
    <>
      <TopGrid>
        <Avatar
          size={AvatarSizeVariant.LARGE}
          avatarSrc={(userData?.avatar as any)?.url}
          disableDropdown
        />
        {canManageUsers ? (
          <ActionsBlock>
            <Dropdown
              items={enabledActions.map((action) => ({
                label: action.label,
                value: action.value,
              }))}
              onChange={handleActionSelect}
              defaultItem={{ label: "Actions", value: "" }}
              leftIcon={<span>⚡</span>}
              disabled={enabledActions.length === 0}
              actionsDropdown
            />
            {actions.filter((action) => action.disabled).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <Typography
                  variant={TypographyVariant.BODY2}
                  style={{ color: "#8181a5", marginBottom: 5 }}
                >
                  Disabled Actions:
                </Typography>
                {actions
                  .filter((action) => action.disabled)
                  .map((action, index) => (
                    <div key={index} style={{ marginBottom: 5 }}>
                      <TooltipText
                        id={`disabled-action-${index}`}
                        text={action.tooltip || "Action is not available"}
                      >
                        <Typography
                          variant={TypographyVariant.BODY2}
                          style={{ color: "#adb5bd", cursor: "not-allowed" }}
                        >
                          {action.label}
                        </Typography>
                      </TooltipText>
                    </div>
                  ))}
              </div>
            )}
          </ActionsBlock>
        ) : (
          <div />
        )}
      </TopGrid>

      <DetailCardsGrid $columns={3}>
        <ConfigurableDetailCard
          config={personalDetailsConfig}
          data={userData}
        />
        {(
          userData?.companyMemberships as unknown as CompanyMembershipFullApiType[]
        )?.map((cm, i) => (
          <ConfigurableDetailCard
            key={cm.company?._id ?? i}
            config={buildUserMembershipCardConfig(i, {
              canManageUsers,
              onActivateClick,
              onBanClick,
              onRoleClick: handleRoleClick,
            })}
            data={cm}
          />
        ))}
      </DetailCardsGrid>

      {confirmationModal?.isOpen && (
        <Modal onHide={closeConfirmationModal}>
          <WarningModal
            headerTitle="Confirm Action"
            mainText={`Are you sure you want to ${confirmationModal.action}?`}
            description="This action cannot be undone."
            cancelButtonText="Cancel"
            deleteButtonText="Confirm"
            onCancel={closeConfirmationModal}
            onContinue={() => {
              confirmationModal.onConfirm();
              closeConfirmationModal();
            }}
          />
        </Modal>
      )}

      {roleModal?.isOpen && (
        <Modal onHide={closeRoleModal}>
          <RolePoliciesModal>
            <Typography
              variant={TypographyVariant.HEADER2}
              style={{ marginBottom: 20 }}
            >
              Role: {roleModal.role?.name}
              <span
                style={{
                  marginLeft: 10,
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 12,
                  backgroundColor: roleModal.role?.isDefaultRole
                    ? "#e3f2fd"
                    : "#fff3e0",
                  color: roleModal.role?.isDefaultRole ? "#1976d2" : "#f57c00",
                }}
              >
                {roleModal.role?.isDefaultRole ? "Default" : "Custom"}
              </span>
            </Typography>
            <Typography
              variant={TypographyVariant.BODY1}
              style={{ marginBottom: 20 }}
            >
              {roleModal.role?.description || "No description available"}
            </Typography>
            <Typography
              variant={TypographyVariant.HEADER3}
              style={{ marginBottom: 15 }}
            >
              Policies (
              {roleModal.policies?.filter(
                (policy: string) => (roleModal.role?.policies as any)?.[policy]
              ).length || 0}{" "}
              enabled)
            </Typography>
            {roleModal.policies && roleModal.policies.length > 0 ? (
              <div>
                {roleModal.policies.map((policy: string, index: number) => {
                  const isEnabled = (roleModal.role?.policies as any)?.[policy];
                  const description =
                    (policyDescriptions as any)[policy] || policy;
                  return (
                    <div key={index} style={{ marginBottom: 8 }}>
                      <Typography
                        variant={TypographyVariant.BODY2}
                        style={{ color: isEnabled ? "#2cd19e" : "#8181a5" }}
                      >
                        {isEnabled ? "✓" : "✗"} {description}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography
                variant={TypographyVariant.BODY2}
                style={{ color: "#8181a5" }}
              >
                No policies enabled for this role
              </Typography>
            )}
          </RolePoliciesModal>
        </Modal>
      )}
    </>
  );
};

export default UserDetails;
